import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, injectIntl, intlFormatPropTypes } from "react-intl";
import { useForm } from "react-hook-form";

import { selectAuthAccessToken } from "../../app-main/selectors/auth.selectors";

import { Button } from "@ps-design-system/components/Button";
import {
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Dialog,
  DialogContent,
} from "@ps-design-system/components/Dialog";
import { Form } from "@ps-design-system/components/Form";
import { InputField } from "@ps-design-system/components/Input";
import { Text } from "@ps-design-system/components/Text";
import { CheckboxField } from "@ps-design-system/components/Checkbox";
import { updateProfile } from "../../app-main/actions/profile";
import {
  HowDidYouHeardOptionType,
  HowDidYouHearFormValues,
} from "./HowDidYouHearModal.types";

type HowDidYouHearModalProps = {
  intl: intlFormatPropTypes;
  defaultValues: HowDidYouHeardOptionType;
  open: boolean;
  options: string[];
  onOpenChange: (open: boolean) => void;
};

const HowDidYouHearModal = ({
  intl,
  defaultValues,
  open,
  options,
  onOpenChange,
}: HowDidYouHearModalProps) => {
  const dispatch = useDispatch();
  const accessToken = useSelector(selectAuthAccessToken);

  // `otherDetail` defaults to "" so the input is controlled from the first
  // render — RHF hands it `undefined` otherwise, which makes React warn about an
  // uncontrolled input becoming controlled on the first keystroke.
  const form = useForm<HowDidYouHearFormValues>({
    defaultValues: defaultValues,
  });

  const handleOnClose = () => {
    // reset the form before the user leaves
    form.reset();
    onOpenChange(false);
  };

  const handleSubmit = async (values: HowDidYouHearFormValues) => {
    form.clearErrors();

    // `Other` and its free-text answer collapse into a single entry, so they are
    // pulled out before mapping the remaining checkboxes to their labels.
    const { Other: isOtherSelected, otherDetail, ...checkboxOptions } = values;

    // RHF only tracks the boxes the user touched, so unselected options are
    // `undefined` rather than `false` — the filter treats both as unselected.
    const selectedOptions = Object.entries(checkboxOptions)
      .filter(([, isSelected]) => isSelected)
      .map(([option]) => option);

    // Typing an answer counts as choosing "Other" even if the box is unchecked.
    const otherAnswer = otherDetail?.trim();
    if (isOtherSelected || otherAnswer) {
      selectedOptions.push(
        otherAnswer ? `Other ${otherAnswer.trim()}` : "Other",
      );
    }
    const payload = {
      host_heard_from: selectedOptions,
    };

    dispatch(updateProfile(accessToken, payload, true))
      .then(() => {
        handleOnClose();
      })
      .catch(() =>
        form.setError("root.serverError", {
          type: "400",
          message: intl.formatMessage({
            id: "hosting.profile_page.save_error",
            defaultMessage:
              "Something went wrong, please try again. If the problem persists please refresh.",
          }),
        }),
      );
  };

  return (
    <Dialog onOpenChange={handleOnClose} open={open}>
      <DialogContent size="md" testId="edit-name-modal">
        <DialogHeader showCloseButton={true}>
          <DialogTitle className="tw-my-0">
            <FormattedMessage
              id="hosting.how_did_you_hear_options.heading"
              defaultMessage="How did you hear about hosting with Peerspace?"
            />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id="hosting.how_did_you_hear_options.select_text"
              defaultMessage="Select all that apply to help us find more hosts like you."
            />
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="tw-pb-16">
          <Form
            {...form}
            fullWidth={true}
            id="heard-about-us-form"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <div className="tw-gap-16 tw-grid tw-grid-cols-2">
              {options.map((option) => (
                <CheckboxField
                  key={option}
                  name={option}
                  // `tw-normal-case` undoes the legacy global `label
                  // { text-transform: lowercase }` rule in styleguide.less,
                  // which otherwise inherits into the checkbox description.
                  description={<Text className="tw-normal-case">{option}</Text>}
                  alignment="top"
                  className="tw-col-span-1"
                />
              ))}
              <span className="tw-col-span-1 tw-flex tw-flex-col">
                <CheckboxField
                  name="Other"
                  description={
                    <Text className="tw-normal-case">
                      <FormattedMessage
                        id="hosting.how_did_you_hear_options.checkbox_label_other"
                        defaultMessage="Other"
                      />
                    </Text>
                  }
                  alignment="top"
                />
                <InputField
                  name="otherDetail"
                  // The "Other" checkbox labels this visually, but it is a separate
                  // control, so it needs its own accessible name.
                  aria-label={intl.formatMessage({
                    id: "hosting.how_did_you_hear_options.other_input_label",
                    defaultMessage:
                      "How else did you hear about hosting with Peerspace?",
                  })}
                  placeholder={intl.formatMessage({
                    id: "hosting.how_did_you_hear_options.other_input_placeholder",
                    defaultMessage: "Tell us more",
                  })}
                />
              </span>
            </div>
            {form.formState?.errors?.root?.serverError && (
              <Text className="tw-text-red-800" element="p" typeface="body-1">
                {form.formState.errors.root.serverError.message}
              </Text>
            )}
          </Form>
        </DialogBody>

        <DialogFooter className="sm:tw-justify-between">
          <Button
            appearance="black"
            onClick={handleOnClose}
            variant="underline-m"
            className="xs:tw-hidden sm:tw-flex"
          >
            <FormattedMessage
              id="hosting.profile_page.cancel_button"
              defaultMessage="Cancel"
            />
          </Button>
          <Button
            appearance="black"
            disabled={form.formState.isSubmitting}
            form="heard-about-us-form"
            showSpinner={form.formState.isSubmitting}
            type="submit"
          >
            <FormattedMessage
              id="hosting.profile_page.save_button"
              defaultMessage="Save"
            />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default injectIntl(HowDidYouHearModal);
